import { yupResolver } from "@hookform/resolvers/yup";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import React, { FC, useEffect, useRef, useState } from "react";
import {
  FormProvider,
  useForm,
  UseFormSetValue,
  useWatch,
} from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useCreateClientMutation,
  useGetBillingDetailsAndConfigByClientIdMutation,
  useGetClientByIdMutation,
  useGetClientHierarchyById,
  useTagParentClientMutation,
  useUpdateClientDetailsMutation,
} from "../../api/services/client/clientApis";
import {
  BillingDetailsAndConfigurationResponse,
  CreateClientParams,
  ErrorModel,
  TagRemoveParentClientParams,
  UpdateClientParams,
} from "../../api/services/client/models";

import {
  AccordionHeader,
  MuiBox,
  MuiButtonPrimary,
  MuiButtonSecondary,
  MuiButtonTertiary,
  MuiGrid,
  MuiModal,
  MuiTypography,
  RowProps,
  WarningConfirmationModal,
} from "../../components";
import { AccordionHeaderText } from "../../components/accordionHeader/AccordionHeaderText";
import ErrorWarning from "../../components/error/ErrorWarning";
import { CountryField } from "../../components/textFieldWithValidation/TextFieldWithValidation";
import {
  BE_ERROR_CODES,
  BILLING_DETAILS_SITE_CORE_VALUE,
  DUPLICATE_DATA_VALUES,
  FORM_ERROR_TIMEOUT_LONG,
  FORM_ERROR_TIMEOUT_SHORT,
  MAX_PERCENTAGE,
  MODULE_PERMISSIONS,
  PAGE_URLS,
} from "../../helpers/constants";
import { COMPONENT_IDS } from "../../helpers/constants/componentIds";
import { commonSiteCoreKeys } from "../../helpers/constants/siteCoreKeys";
import {
  convertToUserFormat,
  convertToUTCFormat,
  findDuplicateProgramTemplateData,
  findFirstDuplicateBillingConfigName,
  findFirstDuplicateData,
  getBooleanValueOrFallback,
  getFormattedAmount,
  getNameByIdFromList,
  getParsedBEErrors,
  getParsedErrorMessage,
  getValueOrBlank,
  getVatPostalDetailsFromCountryKey,
  hasKey,
  isEmpty,
  isNotEmpty,
  isRetailClient,
  isValueEmptyOrDash,
  trimValue,
} from "../../helpers/utils";
import { useSiteCore } from "../../hooks/useSiteCore";
import {
  navigationDataSelector,
  userPreferredLanguageSelector,
} from "../../redux/slices/appState/appStateSelector";
import {
  setCurrencyList,
  setIsDataModifiedCurrentPage,
  setNavigationDataClicked,
  toggleLoader,
} from "../../redux/slices/appState/appStateSlice";
import { createClientSelector } from "../../redux/slices/client/createClientSelector";
import {
  setCreateClientData,
  setIsEditClient,
} from "../../redux/slices/client/createClientSlice";
import ClientSearch from "../clientSearch/ClientSearch";
import { ClientResultRowValue } from "../clientSearch/modelsAndSchemas/Models";
import {
  CreateClientWrapper,
  RemoveParentConfirmatoryPopupWrapper,
} from "./CreateClient.style";
import { ClientContactMain } from "./components/clientContactMain/ClientContactMain";

import dayjs from "dayjs";
import { useGetLoggedInUserDetails } from "../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import PermissionProtectedRoute from "../../aAdminTabTeam2/routes/PermissionProtectedRoute";
import { IdNameList } from "../../api/services/common/models";
import { useGetCurrenciesByCountryId } from "../../api/services/program/programApis";
import PermissionWrapper from "../../components/permissionWrapper/PermissionWrapper";
import { CURRENCY_FORMAT_MAP } from "../../components/textFieldCurrency/CurrencyUtils";
import { getDateFormatByCountry } from "../../helpers/constants/countryDateFormat";
import { usePermissions } from "../../hooks/usePermissions";
import { useToast } from "../../hooks/useToast";
import ClientSearchResultHierarchy from "../clientSearch/components/ClientSearchResultHierarchy/ClientSearchResultHierarchy";
import { CreateClientActivityAndDeliverableMain } from "./components/activitiesAndDeliverables/CreateClientActivityAndDeliverableMain";
import { ClientBillingPurchaseOrderMain } from "./components/clientBillingPurchaseOrderMain/ClientBillingPurchaseOrderMain";
import { ClientDocumentMain } from "./components/clientDocumentMain/ClientDocumentMain";
import { ClientSpecificContentMain } from "./components/clientSpecificContentMain/ClientSpecificContentMain";
import { CreateClientHeader } from "./components/createClientHeader/CreateClientHeader";
import { CreateClientMailingAddressForm } from "./components/createClientMailingAddressForm/CreateClientMailingAddressForm";
import { CreateClientProfileForm } from "./components/createClientProfileForm/CreateClientProfileForm";
import { CreateClientSemiHeader } from "./components/createClientSemiHeader/CreateClientSemiHeader";
import { isContactBelongCurrentClient } from "./helper";
import {
  BillingConfigDuplicateData,
  BillingConfigurationItem,
  BillingConfigurationProgramTemplateItem,
  ClientContact,
  CreateClientFormKey,
  CreateClientSectionTypes,
  ProgramTemplateDuplicateData,
} from "./modelsAndSchemas/Models";
import { CreateClientValidationSchema } from "./modelsAndSchemas/ValidationSchema";
import {
  billingAndPurchaseOrderKeys,
  createClientSiteCoreKeys,
  keyClientDetailsSiteCoreKeys,
} from "./siteCore/SiteCoreKeys";

interface CreateClientProps {
  isEdit?: boolean;
  hasSelfContactProps?: boolean;
}

const CreateClient: FC<CreateClientProps> = ({
  isEdit: isEditClient = false,
  hasSelfContactProps = false,
}: CreateClientProps) => {
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const {
    fetchLabel,
    fetchRichTexts,
    fetchValidationMessage,
    commonLabels,
    fetchComponentTitle,
    commonValidationMessages,
    commonBillingConfigurationDetails,
  } = useSiteCore();

  const navigate = useNavigate();
  const location = useLocation();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );
  const preferredLanguage = useSelector(userPreferredLanguageSelector);

  const [clientCode, setClientCode] = useState("");
  const [showHierarchyModal, setShowHierarchyModal] = useState<boolean>(false);
  const [duplicateErrorMessage, setDuplicateErrorMessage] = useState<string>();
  const [allAccordionStatus, setAllAccordionStatus] = useState(false);
  const [profileAccordionStatus, setProfileAccordionStatus] = useState(true);
  const [mailingAddressAccordionStatus, setMailingAddressAccordionStatus] =
    useState(false);

  const [billingAndPOAccordionStatus, setBillingAndPOAccordionStatus] =
    useState(false);
  const [contactListAccordionStatus, setContactListAccordionStatus] =
    useState(false);
  const [documentAccordionStatus, setDocumentAccordionStatus] = useState(false);
  const [
    clientSpecificContentAccordionStatus,
    setClientSpecificContentAccordionStatus,
  ] = useState(false);
  const [
    clientSpecificActivitiesAccordionStatus,
    setClientSpecificActivitiesAccordionStatus,
  ] = useState(false);

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showTagParentModal, setShowTagParentModal] = useState(false);

  const [showScheduleTotalAmountWarning, setShowScheduleTotalAmountWarning] =
    useState<{
      isOpen: boolean;
      isPercentage: boolean;
    }>({
      isOpen: false,
      isPercentage: false,
    });

  const [
    showRemoveParentConfirmationModal,
    setShowRemoveParentConfirmationModal,
  ] = useState(false);

  const [showBillingCountryModal, setShowBillingCountryModal] = useState(false);
  const [showContactMinimumModal, setShowContactMinimumModal] = useState(false);
  const [
    showTagParentClientContactRemovalModal,
    setShowTagParentClientContactRemovalModal,
  ] = useState(false);

  const removeParentUseRef = useRef(false);

  const [selectedParentClient, setSelectedParentClient] = useState<
    ClientResultRowValue | undefined
  >();
  const [isParentClientRemovalAllowed, setIsParentClientRemovalAllowed] =
    useState(true);
  const tagParentClientRef = useRef<ClientResultRowValue | undefined>();
  const [tagParentClientErrorMessage, setTagParentClientErrorMessage] =
    useState<string>();
  const clientContactMainRef = useRef<{
    getAvailableContactsApi: () => {};
    handleAddContactClick: () => void;
  }>(null);

  const [isBillingConfigurationSaved, setIsBillingConfigurationSaved] =
    useState(false);
  const [isFormReset, setIsFormReset] = useState(false);

  const { id: clientId, clientTypeId } = useSelector(createClientSelector);

  const navigationData = useSelector(navigationDataSelector);

  const [showExitConfirmationModal, setShowExitConfirmationModal] =
    useState(false);

  const [countryVatPostalData, setCountryVatPostalData] = useState<
    CountryField[]
  >([]);
  const [isNewContactAdded, setIsNewContactAdded] = useState(false);
  const [clientContactsData, setClientContactsData] = useState<ClientContact[]>(
    [],
  );
  const [showUpdateWarningPopup, setShowUpdateWarningPopup] = useState(false);
  const [warningErrorMessage, setWarningErrorMessage] = useState("");

  const { contactsTitle } = createClientSiteCoreKeys;

  const {
    keyClientDetailsHeader,
    removeTheParentClientAsAtleastOneContactIsRequired,
  } = keyClientDetailsSiteCoreKeys;

  const getContactsRichLabel = (key: string) => {
    return fetchRichTexts(contactsTitle, key);
  };

  const countryVatPostalDataJson = fetchRichTexts(
    commonSiteCoreKeys.countryBusinessRuleListParent,
    commonSiteCoreKeys.countryBusinessRuleList,
  );

  const [hasSelfContact, setHasSelfContact] = useState(hasSelfContactProps);
  const sectionFieldsMap: Record<CreateClientSectionTypes, string[]> = {
    profile: [
      CreateClientFormKey.companyName,
      CreateClientFormKey.clientType,
      CreateClientFormKey.englishCompanyName,
      CreateClientFormKey.industry,
      CreateClientFormKey.localClientRelationshipOwnerId,
      CreateClientFormKey.addressCountry,
      CreateClientFormKey.billingCountry,
      CreateClientFormKey.taxVat,
      CreateClientFormKey.businessIdentificationNumber,
      CreateClientFormKey.dedicatedProjectManager,
      CreateClientFormKey.originDate,
    ],
    clientMailingAddress: [
      CreateClientFormKey.address1,
      CreateClientFormKey.address2,
      CreateClientFormKey.address3,
      CreateClientFormKey.country,
      CreateClientFormKey.city,
      CreateClientFormKey.stateProvince,
      CreateClientFormKey.postalCode,
    ],
    billingConfigurations: [
      CreateClientFormKey.invoiceLanguage,
      CreateClientFormKey.earlyPayDays,
      CreateClientFormKey.earlyPayPercentage,
      CreateClientFormKey.configurations,
    ],
  };

  const methods = useForm({
    mode: "onBlur",
    defaultValues: {
      [CreateClientFormKey.companyName]: "",
      [CreateClientFormKey.clientType]: "",
      [CreateClientFormKey.englishCompanyName]: "",
      [CreateClientFormKey.industry]: "",
      [CreateClientFormKey.localClientRelationshipOwnerId]: "",
      [CreateClientFormKey.dedicatedProjectManager]: "",
      [CreateClientFormKey.addressCountry]: "",
      [CreateClientFormKey.addressCountryName]: "",
      [CreateClientFormKey.billingCountry]: "",
      [CreateClientFormKey.billingCountryName]: "",
      [CreateClientFormKey.taxVat]: "",
      [CreateClientFormKey.address1]: "",
      [CreateClientFormKey.address2]: "",
      [CreateClientFormKey.address3]: "",
      [CreateClientFormKey.country]: "",
      [CreateClientFormKey.countryName]: "",
      [CreateClientFormKey.city]: "",
      [CreateClientFormKey.stateProvince]: "",
      [CreateClientFormKey.postalCode]: "",
      [CreateClientFormKey.siretNumber]: "",
      [CreateClientFormKey.btwNummer]: "",
      [CreateClientFormKey.nif]: "",
      [CreateClientFormKey.abnNumber]: "",
      [CreateClientFormKey.nzbn]: "",
      [CreateClientFormKey.qualifiedInvoiceNumber]: "",
      [CreateClientFormKey.cin]: "",
      [CreateClientFormKey.bin]: "",
      [CreateClientFormKey.tin]: "",
      [CreateClientFormKey.consolidatedInvoicing]: false,
      [CreateClientFormKey.rebateClient]: false,
      [CreateClientFormKey.earlyPayDiscount]: false,
      [CreateClientFormKey.activeCompany]: true,
      [CreateClientFormKey.earlyPayDays]: undefined,
      [CreateClientFormKey.earlyPayPercentage]: undefined,
      [CreateClientFormKey.notes]: "",
      [CreateClientFormKey.configurations]: [],
      [CreateClientFormKey.businessIdentificationNumber]: "",
      [CreateClientFormKey.originDate]: dayjs().format(userCountryDateFormat),
      [CreateClientFormKey.invoiceLanguage]: "",
    },
    resolver: yupResolver(
      CreateClientValidationSchema(
        fetchValidationMessage,
        fetchLabel,
        clientCode,
        preferredLanguage,
      ),
      {},
    ),
  });

  const {
    setValue,
    getValues,
    handleSubmit,
    reset,
    trigger,
    formState,
    setError,
  } = methods;

  const {
    mutateAsync: getClientById,
    data: clientData,
    isPending: isGetClientByIdPending,
    isSuccess: isGetClientByIdSuccess,
  } = useGetClientByIdMutation();

  const {
    mutateAsync: getBillingDetailsAndConfigByClientId,
    data: clientBillingData,
    isPending: isBillingDetailsGetApiPending,
    isSuccess: isBillingDetailsGetApiSuccess,
  } = useGetBillingDetailsAndConfigByClientIdMutation();

  const {
    mutateAsync: getClientHierarchyData,
    isPending: isClientHierarchyPending,
    isSuccess: isClientHierarchySuccess,
    isError: isClientHierarchyError,
    data: clientHierarchyResult,
  } = useGetClientHierarchyById();

  const {
    data: currenciesList,
    isSuccess: isCurrenciesListSuccess,
    refetchWithParams,
  } = useGetCurrenciesByCountryId();

  const hasErrors = (section: CreateClientSectionTypes) => {
    const fields = sectionFieldsMap[section];
    return fields.some(
      (field) => formState.errors[field as keyof typeof formState.errors],
    );
  };

  const checkErrorsAndExpandSection = () => {
    (Object.keys(sectionFieldsMap) as CreateClientSectionTypes[]).forEach(
      (sectionKey: CreateClientSectionTypes) => {
        if (hasErrors(sectionKey)) {
          switch (sectionKey) {
            case "profile": {
              setProfileAccordionStatus(true);
              break;
            }
            case "clientMailingAddress": {
              setMailingAddressAccordionStatus(true);
              break;
            }
            case "billingConfigurations": {
              setBillingAndPOAccordionStatus(true);
              break;
            }
          }
        }
      },
    );
  };

  useEffect(() => {
    checkErrorsAndExpandSection();
  }, [formState.errors]);

  useEffect(() => {
    if (isCurrenciesListSuccess && isNotEmpty(currenciesList)) {
      dispatch(setCurrencyList(currenciesList));
    }
  }, [isCurrenciesListSuccess]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isGetClientByIdPending || isBillingDetailsGetApiPending,
      }),
    );
  }, [isGetClientByIdPending, isBillingDetailsGetApiPending]);

  const getClientDetails = (clientId: string) => {
    getClientById(clientId);
    getBillingDetailsAndConfigByClientId(clientId);
  };

  useEffect(() => {
    dispatch(
      setIsEditClient({
        isEdit: isEditClient,
      }),
    );

    const clientIdForEdit = location?.state?.clientId;
    setProfileAccordionStatus(!isEditClient);
    if (isEditClient && clientIdForEdit) {
      getClientDetails(clientIdForEdit);
    } else {
      reset({}, { keepDefaultValues: true });
      setClientCode("");
      setCreateClientData({
        clientCode: "",
        id: "",
        clientTypeId: "",
        clientName: "",
      });
      setSelectedParentClient(undefined);
      tagParentClientRef.current = undefined;
      setIsBillingConfigurationSaved(false);
    }
    return () => {
      dispatch(
        setIsEditClient({
          isEdit: false,
        }),
      );
    };
  }, [isEditClient]);

  const fetchCountryByBillingCountryId = (countryId: string) => {
    refetchWithParams(countryId);
  };

  useEffect(() => {
    const clientIdForEdit = isEditClient ? location?.state?.clientId : clientId;
    if (isGetClientByIdSuccess && clientData && isNotEmpty(clientData.data)) {
      const { mailingAddress, profile } = clientData.data;
      setIsParentClientRemovalAllowed(profile?.isParentClientRemovalAllowed);
      setClientCode(profile.clientCode);
      // here fetching currency list base on billing country
      fetchCountryByBillingCountryId(profile.billingCountryId);
      dispatch(
        setCreateClientData({
          id: clientIdForEdit,
          clientCode: profile.clientCode,
          clientTypeId: profile.clientTypeId,
          clientName: profile.clientName,
        }),
      );

      if (
        profile?.parentClientId &&
        profile?.parentClientCode &&
        profile?.parentClientName
      ) {
        const parentClientDetails = {
          id: profile?.parentClientId,
          clientCode: profile?.parentClientCode,
          companyName: profile?.parentClientName,
        };
        setSelectedParentClient(parentClientDetails);
        tagParentClientRef.current = parentClientDetails;
      }

      setValue(CreateClientFormKey.companyName, profile.clientName);
      setValue(CreateClientFormKey.clientType, profile.clientTypeId);
      setValue(
        CreateClientFormKey.englishCompanyName,
        getValueOrBlank(profile?.englishName),
      );
      setValue(CreateClientFormKey.industry, profile.industryId);
      setValue(
        CreateClientFormKey.localClientRelationshipOwnerId,
        profile.localClientRelationshipOwnerId,
      );
      setValue(
        CreateClientFormKey.dedicatedProjectManager,
        getValueOrBlank(profile?.dedicatedProjectManagerId),
      );
      setValue(CreateClientFormKey.addressCountry, profile.addressCountryId);
      setValue(
        CreateClientFormKey.addressCountryName,
        profile.addressCountryName,
      );
      setValue(CreateClientFormKey.billingCountry, profile.billingCountryId);
      setValue(
        CreateClientFormKey.billingCountryName,
        profile.billingCountryName,
      );

      setValue(CreateClientFormKey.address1, mailingAddress.address1);
      setValue(
        CreateClientFormKey.address2,
        getValueOrBlank(mailingAddress?.address2),
      );
      setValue(
        CreateClientFormKey.address3,
        getValueOrBlank(mailingAddress?.address3),
      );
      setValue(
        CreateClientFormKey.country,
        getValueOrBlank(mailingAddress?.countryId),
      );
      setValue(
        CreateClientFormKey.countryName,
        getValueOrBlank(profile?.addressCountryName),
      );
      setValue(CreateClientFormKey.city, mailingAddress.city);
      setValue(
        CreateClientFormKey.stateProvince,
        getValueOrBlank(mailingAddress?.stateId),
      );
      setValue(
        CreateClientFormKey.originDate,
        convertToUserFormat(profile.originDate, userCountryDateFormat),
      );

      setTimeout(() => {
        setValue(
          CreateClientFormKey.taxVat,
          getValueOrBlank(profile?.vatNumber),
        );
        setValue(
          CreateClientFormKey.postalCode,
          getValueOrBlank(mailingAddress?.postalCode),
        );
        setValue(
          CreateClientFormKey.businessIdentificationNumber,
          getValueOrBlank(profile?.businessIdentificationNumber),
        );
        setValue(CreateClientFormKey.salesOfficeId, profile.salesOffice?.id);
        setValue(
          CreateClientFormKey.salesOfficeName,
          profile.salesOffice?.name,
        );
      }, FORM_ERROR_TIMEOUT_LONG);
    }
  }, [clientData, isGetClientByIdSuccess]);

  useEffect(() => {
    if (
      isBillingDetailsGetApiSuccess &&
      clientBillingData &&
      isNotEmpty(clientBillingData.data)
    ) {
      const { billingDetails } =
        clientBillingData?.data as BillingDetailsAndConfigurationResponse;

      setValue(
        CreateClientFormKey.consolidatedInvoicing,
        !!billingDetails?.hasConsolidatedInvoicing,
      );
      setValue(
        CreateClientFormKey.rebateClient,
        !!billingDetails?.isRebateClient,
      );
      setValue(
        CreateClientFormKey.earlyPayDiscount,
        !!billingDetails?.hasEarlyPayDiscount,
      );
      setValue(
        CreateClientFormKey.activeCompany,
        getBooleanValueOrFallback(billingDetails?.isActiveCompany, true),
      );
      setValue(
        CreateClientFormKey.invoiceLanguage,
        getValueOrBlank(billingDetails?.invoiceLanguage?.id),
      );

      if (billingDetails?.hasEarlyPayDiscount) {
        if (isNotEmpty(billingDetails?.earlyPayDays?.toString())) {
          setValue(
            CreateClientFormKey.earlyPayDays,
            billingDetails?.earlyPayDays?.toString(),
          );
        }

        if (isNotEmpty(billingDetails?.earlyPayPercentage?.toString())) {
          setValue(
            CreateClientFormKey.earlyPayPercentage,
            getValueOrBlank(billingDetails?.earlyPayPercentage?.toString()),
          );
        }
      }

      setValue(
        CreateClientFormKey.notes,
        getValueOrBlank(billingDetails?.notes),
      );

      setBillingDetailsValues(billingDetails?.billingConfigurations, setValue);
    }
  }, [clientBillingData, isBillingDetailsGetApiSuccess]);

  const handleScheduleAmountWarningModalClose = () => {
    setShowScheduleTotalAmountWarning((prev) => ({ ...prev, isOpen: false }));
  };

  // Helper function to set values for program template configurations
  const setProgramTemplateConfigValues = (
    configIndex: number,
    programConfig: BillingConfigurationProgramTemplateItem,
    templateIndex: number,
    setValue: UseFormSetValue<any>,
    currenciesList?: IdNameList[],
  ) => {
    const fieldPrefix = `${CreateClientFormKey.configurations}.${configIndex}.${CreateClientFormKey.programTemplateConfigurations}.${templateIndex}`;

    // Set feePaymentType immediately (remove trailing space typo)
    setValue(
      `${fieldPrefix}.${CreateClientFormKey.feePaymentType}`,
      programConfig?.feePaymentType,
    );

    // Set billingSchedules immediately
    setValue(
      `${fieldPrefix}.${CreateClientFormKey.billingSchedules}`,
      programConfig?.billingSchedules,
    );

    // First timeout: Set currencyId and fee
    setTimeout(() => {
      setValue(
        `${fieldPrefix}.${CreateClientFormKey.currencyId}`,
        programConfig?.currency?.id,
      );

      // Second timeout: Set feeWithFormat AFTER currencyId and fee are set
      setTimeout(() => {
        const currencyNameById = getNameByIdFromList(
          currenciesList,
          getValueOrBlank(programConfig?.currency?.id),
        );
        const feeWithFormat = `${getFormattedAmount(
          Number(programConfig?.fee),
          CURRENCY_FORMAT_MAP[currencyNameById]?.locale,
        )}`;

        setValue(
          `${fieldPrefix}.${CreateClientFormKey.feeWithFormat}`,
          feeWithFormat,
        );
        setValue(
          `${fieldPrefix}.${CreateClientFormKey.fee}`,
          programConfig?.fee,
        );
      }, FORM_ERROR_TIMEOUT_SHORT);
    }, FORM_ERROR_TIMEOUT_SHORT);
  };
  const setBillingDetailsValuesProgramTemplates = (
    config: BillingConfigurationItem,
    configIndex: number,
    setValue: UseFormSetValue<any>,
    currenciesList?: IdNameList[],
  ) => {
    if (Array.isArray(config.programTemplateConfigurations)) {
      config.programTemplateConfigurations.forEach(
        (programConfig, templateIndex) => {
          setProgramTemplateConfigValues(
            configIndex,
            programConfig,
            templateIndex,
            setValue,
            currenciesList,
          );
        },
      );
    }
  };

  const setBillingDetailsValues = (
    billingConfigurations: BillingConfigurationItem[],
    setValue: UseFormSetValue<any>,
  ) => {
    if (isNotEmpty(billingConfigurations)) {
      setIsBillingConfigurationSaved(isNotEmpty(billingConfigurations));
      billingConfigurations.forEach((item) => {
        item.paymentTermId = item.paymentTerms?.id;
        item.isOpportunityIdValidated = isNotEmpty(item.opportunityId);
      });

      setValue(CreateClientFormKey.configurations, billingConfigurations);
      setTimeout(() => {
        billingConfigurations.forEach((config, configIndex) => {
          setBillingDetailsValuesProgramTemplates(
            config,
            configIndex,
            setValue,
            currenciesList,
          );
        });
      }, 2000);
    }
  };

  const dedicatedProjectManagerWatch = useWatch({
    name: CreateClientFormKey.dedicatedProjectManager,
    control: methods.control,
  });

  useEffect(() => {
    const { configurations, dedicatedProjectManager } = getValues();
    if (
      !isEditClient &&
      configurations?.length === 1 &&
      isNotEmpty(dedicatedProjectManager) &&
      isEmpty(configurations[0].projectManagerId)
    ) {
      setValue(CreateClientFormKey.configurations, [
        {
          ...configurations[0],
          projectManagerId: getValueOrBlank(
            getValues(CreateClientFormKey.dedicatedProjectManager),
          ),
        },
      ]);
    }
  }, [dedicatedProjectManagerWatch]);

  const {
    mutate: createClient,
    isPending,
    data: createClientData,
    isSuccess: isCreateClientApiSuccess,
    isError: isCreateClientError,
    error: createClientError,
  } = useCreateClientMutation();

  const {
    mutate: tagParentClientApi,
    isPending: isTagParentClientApiPending,
    isSuccess: isTagParentClientApiSuccess,
    isError: isTagParentClientApiError,
    error: tagParentClientApiError,
  } = useTagParentClientMutation();

  const {
    mutateAsync: updateClient,
    isPending: isUpdateClientPending,
    isSuccess: updateClientSuccess,
    isError: isUpdateClientError,
    error: updateClientError,
  } = useUpdateClientDetailsMutation();

  const showErrorsFromBE = (error: ErrorModel) => {
    switch (error.errorCode) {
      case BE_ERROR_CODES.PO_BALANCE_EXHAUSTED:
        const errorMessage = fetchLabel(
          billingAndPurchaseOrderKeys.billingDetailsAndPOTitle,
          billingAndPurchaseOrderKeys.poBalanceExhaustedErrorMessage,
        );
        displayToast(errorMessage, "error");
        break;
      case BE_ERROR_CODES.PARENT_CLIENT_ALREADY_IN_USE:
        setWarningErrorMessage(
          fetchLabel(
            billingAndPurchaseOrderKeys.billingDetailsAndPOTitle,
            billingAndPurchaseOrderKeys.parentClientInactiveWarningMessage,
          ),
        );
        setShowUpdateWarningPopup(true);
        break;
      case BE_ERROR_CODES.MULTIPLE_BILLING_CONFIG_EXISTS_FOR_SAME_PRODUCT_LINE:
        setWarningErrorMessage(
          fetchLabel(
            billingAndPurchaseOrderKeys.billingDetailsAndPOTitle,
            billingAndPurchaseOrderKeys.multipleBillingConfigValidationMessage,
          ),
        );
        setShowUpdateWarningPopup(true);
        break;
    }
  };

  useEffect(() => {
    if (isUpdateClientError && updateClientError) {
      const error = getParsedBEErrors(updateClientError)[0];
      if (isNotEmpty(error) && hasKey(error, "errorCode")) {
        showErrorsFromBE(error);
      } else {
        displayToast(
          commonLabels.someThingWentWrongPleaseTryAgainLater,
          "error",
        );
      }
    }
  }, [isUpdateClientError, updateClientError]);

  useEffect(() => {
    return () => {
      reset(
        {
          configurations: [],
        },
        { keepValues: false, keepTouched: false, keepDefaultValues: true },
      );
    };
  }, [isEditClient]);

  useEffect(() => {
    return () => {
      dispatch(setIsDataModifiedCurrentPage(false));
    };
  }, []);

  useEffect(() => {
    if (updateClientSuccess) {
      displayToast(commonLabels.changesSavedMessage);

      dispatch(setIsDataModifiedCurrentPage(false));
      navigate(`${PAGE_URLS.VIEW_CLIENT}/${clientId}`, {
        state: { clientId: clientId, from: PAGE_URLS.SEARCH_CLIENT },
      });
    }
  }, [updateClientSuccess]);

  useEffect(() => {
    if (countryVatPostalDataJson) {
      const countryVatPostalDataJsonParsed: CountryField[] = JSON.parse(
        countryVatPostalDataJson,
      );
      setCountryVatPostalData(countryVatPostalDataJsonParsed);
    }
  }, [countryVatPostalDataJson]);

  const handleCloseClick = () => {
    if (isNotEmpty(formState.touchedFields)) {
      setShowCancelModal(true);
    }
  };

  const handleCancelClick = () => {
    if (isEditClient) {
      dispatch(
        setIsEditClient({
          isEdit: false,
        }),
      );
    }

    if (isNotEmpty(formState.touchedFields)) {
      setShowCancelModal(true);
    } else navigate(-1);
  };

  const handleUpdateClick = () => {
    handleSaveClick();
  };

  const checkAndValidateOpportunityId = (): boolean => {
    const configurations = getValues().configurations;
    if (!configurations) return true;

    let isValid = true;

    configurations.forEach((configData, index) => {
      const { opportunityId, isOpportunityIdValidated } = configData;

      if (isNotEmpty(opportunityId) && !isOpportunityIdValidated) {
        const fieldName =
          `${CreateClientFormKey.configurations}[${index}].${CreateClientFormKey.opportunityId}` as any;
        const configError = formState.errors?.configurations?.[index];

        // Only set error if one doesn't already exist for this field
        const hasExistingOpportunityIdError =
          configError &&
          typeof configError === "object" &&
          "opportunityId" in configError &&
          configError.opportunityId;

        if (!hasExistingOpportunityIdError) {
          setError(fieldName, {
            type: "custom",
            message: fetchLabel(
              billingAndPurchaseOrderKeys.billingDetailsAndPOTitle,
              billingAndPurchaseOrderKeys.opportunityIDMustBeValidatedMessage,
            ),
          });
        }

        isValid = false;
      }
    });

    return isValid;
  };

  const handleBillingModal = () => {
    setTimeout(() => {
      if (isNotEmpty(formState.errors)) {
        checkErrorsAndExpandSection();
        return;
      }

      trigger().then((isValid) => {
        if (!checkTaxVatPostalValidation()) {
          checkErrorsAndExpandSection();
          return;
        }
        if (!isValid) {
          handleSubmit(handleContinueClick)();
        } else if (isValid && clientCode) {
          handleSubmit(handleContinueClick)();
        } else {
          setShowBillingCountryModal(true);
        }
      });
    }, FORM_ERROR_TIMEOUT_LONG);
  };

  const handleSaveClick = () => {
    trigger().then((isValid) => {
      if (!hasSelfContact) {
        setShowContactMinimumModal(true);
        return;
      }

      if (!checkTaxVatPostalValidation()) {
        return;
      }

      if (!checkAndValidateOpportunityId()) {
        return;
      }

      if (isValid && (isNotEmpty(formState.touchedFields) || isEditClient)) {
        setShowSaveModal(true);
      } else {
        checkErrorsAndExpandSection();
      }
    });
  };

  const hasDuplicateBillingConfig = () => {
    const { configurations } = getValues();

    let duplicateData: BillingConfigDuplicateData | undefined;
    duplicateData = findFirstDuplicateBillingConfigName(configurations!!);

    const {
      billingDetailsAndPOTitle,
      duplicateRecordBillingConfigurationError,
      thisBillingConfigurationAlreadyExists,
    } = billingAndPurchaseOrderKeys;

    if (duplicateData) {
      duplicateData.duplicateIndexes.forEach((duplicateIndex) => {
        const formIdPrefix = `${CreateClientFormKey.configurations}[${duplicateIndex}]`;
        const formIdField = `${formIdPrefix}.${CreateClientFormKey.billingConfigurationName}`;
        setTimeout(() => {
          setError(formIdField as any, {
            type: "custom",
            message: fetchLabel(
              billingDetailsAndPOTitle,
              thisBillingConfigurationAlreadyExists,
            ),
          });
        }, FORM_ERROR_TIMEOUT_SHORT);
      });
    }

    if (!duplicateData)
      duplicateData = findFirstDuplicateData(
        configurations!!,
        DUPLICATE_DATA_VALUES.BILLING_CONFIGURATION,
        undefined,
        ["id", "billingConfigurationName"],
      );

    if (
      duplicateData?.duplicateIndexes &&
      isNotEmpty(duplicateData?.duplicateIndexes)
    ) {
      const errorMessageFromSiteCore = fetchLabel(
        billingDetailsAndPOTitle,
        duplicateRecordBillingConfigurationError,
      );

      setDuplicateErrorMessage(errorMessageFromSiteCore);
    } else {
      // for clear errors
      setDuplicateErrorMessage(undefined);
    }

    return isNotEmpty(duplicateData?.duplicateIndexes);
  };
  const hasDuplicateProgramTemplate = () => {
    const { configurations } = getValues();

    let duplicateProgramTemplateData: ProgramTemplateDuplicateData | undefined =
      findDuplicateProgramTemplateData(
        configurations!!,
        "programTemplateConfigurations",
      );

    const {
      billingDetailsAndPOTitle,
      duplicateRecordBillingConfigProgramTemplateError,
      thisProgramTemplateAlreadyExists,
    } = billingAndPurchaseOrderKeys;

    if (isNotEmpty(duplicateProgramTemplateData)) {
      duplicateProgramTemplateData.duplicateDataDetails.forEach(
        (duplicateData) => {
          const formIdPrefix = `${CreateClientFormKey.configurations}.${duplicateData.parentIndex}.${CreateClientFormKey.programTemplateConfigurations}.${duplicateData.programTemplateIndex}`;
          const formIdField = `${formIdPrefix}.${CreateClientFormKey.programTemplateId}`;
          setTimeout(() => {
            setError(formIdField as any, {
              type: "custom",
              message: fetchLabel(
                billingDetailsAndPOTitle,
                thisProgramTemplateAlreadyExists,
              ),
            });
          }, FORM_ERROR_TIMEOUT_SHORT);
        },
      );

      const errorMessageFromSiteCore = fetchLabel(
        billingDetailsAndPOTitle,
        duplicateRecordBillingConfigProgramTemplateError,
      );

      setDuplicateErrorMessage(errorMessageFromSiteCore);
    } else {
      // for clear errors
      setDuplicateErrorMessage(undefined);
    }
    return isNotEmpty(duplicateProgramTemplateData);
  };

  const handleSaveConfirmClick = () => {
    if (hasDuplicateBillingConfig()) {
      return;
    }

    if (hasDuplicateProgramTemplate()) {
      return;
    }
    if (!validateConfigurationDetails()) {
      return;
    }

    const {
      activeCompany,
      consolidatedInvoicing,
      earlyPayDays,
      earlyPayDiscount,
      earlyPayPercentage,
      rebateClient,
      notes,
      configurations,
      invoiceLanguage,
    } = getValues();

    const createClientParams = getCreateClientParams();
    const billingParams = getBillingParams(configurations || []);

    const params: UpdateClientParams = {
      clientId,
      client: createClientParams.client,

      billingDetails: {
        hasConsolidatedInvoicing: !!consolidatedInvoicing,
        isActiveCompany: !!activeCompany,
        isRebateClient: !!rebateClient,
        hasEarlyPayDiscount: !!earlyPayDiscount,
        earlyPayDays:
          isNotEmpty(earlyPayDays) && !!earlyPayDiscount
            ? Number(earlyPayDays)
            : undefined,
        earlyPayPercentage:
          isNotEmpty(earlyPayPercentage) && !!earlyPayDiscount
            ? Number(earlyPayPercentage)
            : undefined,
        notes: notes?.toString(),
        billingConfigurations: billingParams,
        ...(invoiceLanguage && {
          invoiceLanguageId: invoiceLanguage,
        }),
      },
    };
    updateClient(params);
  };

  useEffect(() => {
    if (isCreateClientApiSuccess) {
      setClientCode(createClientData.data.clientCode);
      setProfileAccordionStatus(false);
      setMailingAddressAccordionStatus(false);
      dispatch(
        setCreateClientData({
          ...createClientData.data,
          clientTypeId: clientTypeId,
          clientName: getValues("companyName"),
        }),
      );
      clientContactMainRef.current?.getAvailableContactsApi();
      setContactListAccordionStatus(true);
      reset(
        {},
        { keepValues: true, keepTouched: false, keepDefaultValues: true },
      );
      const { billingCountry } = getValues();
      fetchCountryByBillingCountryId(billingCountry);
    }
  }, [createClientData, isCreateClientApiSuccess]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isPending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isPending]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isUpdateClientPending,
      }),
    );
  }, [isUpdateClientPending]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isTagParentClientApiPending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
  }, [isTagParentClientApiPending]);

  useEffect(() => {
    if (isTagParentClientApiSuccess) {
      const message = removeParentUseRef.current
        ? fetchLabel(
            keyClientDetailsSiteCoreKeys.keyClientDetailsHeader,
            keyClientDetailsSiteCoreKeys.parentClientRemoved,
          )
        : fetchLabel(
            keyClientDetailsSiteCoreKeys.keyClientDetailsHeader,
            keyClientDetailsSiteCoreKeys.parentClientTagged,
          );
      displayToast(message);
      if (removeParentUseRef.current) {
        setSelectedParentClient(undefined);
        tagParentClientRef.current = undefined;
        hideRemoveParentConfirmationModal();
      } else {
        setSelectedParentClient(tagParentClientRef.current);
        setShowTagParentModal(false);
        setTagParentClientErrorMessage(undefined);
      }
      clientContactMainRef.current?.getAvailableContactsApi();
    }
  }, [isTagParentClientApiSuccess]);

  useEffect(() => {
    if (isTagParentClientApiError && tagParentClientApiError) {
      let errorMessage = getParsedErrorMessage(tagParentClientApiError);
      setShowTagParentModal(false);
      setTagParentClientErrorMessage(undefined);
      displayToast(errorMessage);
    }
  }, [isTagParentClientApiError]);

  useEffect(() => {
    if (isCreateClientApiSuccess) {
      const clientCreatedMessage = fetchLabel(
        createClientSiteCoreKeys.clientMailingAddressTitle,
        createClientSiteCoreKeys.clientCreatedMessage,
      );
      displayToast(clientCreatedMessage);
    }
  }, [isCreateClientApiSuccess]);

  useEffect(() => {
    dispatch(
      setIsDataModifiedCurrentPage(!!isNotEmpty(formState.touchedFields)),
    );
  }, [formState]);

  useEffect(() => {
    if (navigationData.onNavigationClicked) {
      setShowExitConfirmationModal(true);
    }
  }, [navigationData.onNavigationClicked]);

  const getCreateClientParams = () => {
    const {
      companyName,
      englishCompanyName,
      clientType,
      industry,
      localClientRelationshipOwnerId,
      addressCountry,
      addressCountryName,
      billingCountry,
      billingCountryName,
      taxVat,
      dedicatedProjectManager,
      address1,
      address2,
      address3,
      stateProvince,
      city,
      postalCode = "",
      siretNumber,
      btwNummer,
      nif,
      abnNumber,
      nzbn,
      qualifiedInvoiceNumber,
      cin,
      bin,
      tin,
      originDate,
      salesOfficeId,
    } = getValues();

    const siretNumberValue = isNotEmpty(siretNumber)
      ? siretNumber?.replaceAll(" ", "")
      : undefined;

    const businessIdentificationNumber = [
      siretNumberValue,
      btwNummer,
      nif,
      abnNumber,
      nzbn,
      qualifiedInvoiceNumber,
      cin,
      bin,
      tin,
    ].find(isNotEmpty);

    const englishCompanyNameValue = isNotEmpty(englishCompanyName)
      ? englishCompanyName
      : companyName;

    const params: CreateClientParams = {
      client: {
        profile: {
          clientName: trimValue(companyName),
          parentClientId: selectedParentClient?.id,
          englishName: englishCompanyNameValue,
          clientTypeId: trimValue(clientType),
          industryId: trimValue(industry),
          localClientRelationshipOwnerId: trimValue(
            localClientRelationshipOwnerId,
          ),
          addressCountryId: trimValue(addressCountry),
          addressCountryName: trimValue(addressCountryName),
          billingCountryId: trimValue(billingCountry),
          billingCountryName: trimValue(billingCountryName),
          vatNumber: trimValue(taxVat),
          ...(dedicatedProjectManager && {
            dedicatedProjectManagerId: trimValue(dedicatedProjectManager),
          }),
          ...(businessIdentificationNumber && {
            businessIdentificationNumber: businessIdentificationNumber,
          }),
          originDate: isEditClient
            ? convertToUTCFormat(originDate!!, userCountryDateFormat)
            : convertToUTCFormat(
                dayjs().format(userCountryDateFormat),
                userCountryDateFormat,
              ),
          salesOfficeId,
        },
        mailingAddress: {
          address1: address1,
          address2: address2,
          address3: address3,
          city: city,
          postalCode: postalCode,
          stateId: stateProvince,
          countryId: trimValue(addressCountry),
        },
      },
    };

    return params;
  };

  const getBillingParams = (config: BillingConfigurationItem[]) => {
    const params = config.map((item) => ({
      ...item,
      opportunityId: !isValueEmptyOrDash(getValueOrBlank(item.opportunityId))
        ? item.opportunityId
        : undefined,
      opportunityName: !isValueEmptyOrDash(
        getValueOrBlank(item.opportunityName),
      )
        ? item.opportunityName
        : undefined,
    }));

    return params;
  };

  const checkIsInvalidateProgramTemplates = (
    programTemplateConfigurations: BillingConfigurationProgramTemplateItem[],
  ) => {
    if (isEmpty(programTemplateConfigurations)) {
      return false;
    }
    return programTemplateConfigurations.some((programTemplate) => {
      if (
        [
          BILLING_DETAILS_SITE_CORE_VALUE.billingTypeMilestone,
          BILLING_DETAILS_SITE_CORE_VALUE.billingTypeSchedule,
        ].includes(programTemplate.billingTypeId)
      ) {
        let totalAmountOrPercentage = 0;
        const isPercentage =
          programTemplate.feePaymentType ===
          BILLING_DETAILS_SITE_CORE_VALUE.valuePercentage;
        programTemplate.billingSchedules?.forEach((item) => {
          totalAmountOrPercentage += isPercentage
            ? Number(item.percentage)
            : Number(item?.amount);
        });
        if (
          (isPercentage && totalAmountOrPercentage === MAX_PERCENTAGE) ||
          (!isPercentage &&
            Number(programTemplate.fee) === totalAmountOrPercentage)
        ) {
          return false;
        } else {
          setShowScheduleTotalAmountWarning({
            isOpen: true,
            isPercentage,
          });
          return true;
        }
      }
      return false;
    });
  };

  // check billing schedule/milestone amount or peasant sum to 100%
  const validateConfigurationDetails = (): boolean => {
    const { configurations } = getValues();

    if (isEmpty(configurations)) {
      return true;
    }
    const isInvalidConfig = configurations!.some((config) => {
      if (isEmpty(config?.programTemplateConfigurations)) {
        return false;
      }
      return checkIsInvalidateProgramTemplates(
        config?.programTemplateConfigurations,
      );
    });
    return !isInvalidConfig;
  };

  const handleContinueClick = () => {
    if (validateConfigurationDetails()) {
      const payload = getCreateClientParams();
      createClient(payload);
    }
  };

  const checkTaxVatPostalValidation = () => {
    const { addressCountry, billingCountry, taxVat, postalCode } = getValues();
    const { errors } = formState;

    let isValid = true;

    const addressCountryRules = getVatPostalDetailsFromCountryKey(
      addressCountry,
      countryVatPostalData,
    );
    if (addressCountryRules?.postalCode.isMandatory && !postalCode) {
      setTimeout(() => {
        setError(CreateClientFormKey.postalCode, {
          type: "custom",
          message: fetchValidationMessage(
            commonSiteCoreKeys.requiredValidation,
          ),
        });
      }, FORM_ERROR_TIMEOUT_SHORT);

      isValid = false;
    } else if (errors.postalCode) {
      setTimeout(() => {
        setError(CreateClientFormKey.postalCode, errors.postalCode!);
      }, FORM_ERROR_TIMEOUT_SHORT);
      isValid = false;
    }

    const billingCountryRules = getVatPostalDetailsFromCountryKey(
      billingCountry,
      countryVatPostalData,
    );
    if (billingCountryRules?.taxIDOrVATNumber.isMandatory && !taxVat) {
      setTimeout(() => {
        setError(CreateClientFormKey.taxVat, {
          type: "custom",
          message: fetchValidationMessage(
            commonSiteCoreKeys.requiredValidation,
          ),
        });
      }, FORM_ERROR_TIMEOUT_SHORT);
      isValid = false;
    } else if (errors.taxVat) {
      setTimeout(() => {
        setError(CreateClientFormKey.taxVat, errors.taxVat!);
      }, FORM_ERROR_TIMEOUT_SHORT);
      isValid = false;
    }

    if (!isValid) {
      setTimeout(() => {
        checkErrorsAndExpandSection();
      }, FORM_ERROR_TIMEOUT_LONG);
    }
    return isValid;
  };

  const getRemoveIcon = () => {
    return <RemoveIcon color={"primary"} />;
  };

  const getAddIcon = () => {
    return <AddIcon color={"primary"} />;
  };

  const handleTagParentClientModalClose = () => {
    setShowTagParentModal(false);
    tagParentClientRef.current = undefined;
    setTagParentClientErrorMessage(undefined);
  };

  const callTagParentClientApi = (type: "tag" | "remove") => {
    if (
      isNotEmpty(tagParentClientRef.current) &&
      tagParentClientRef.current?.id &&
      isNotEmpty(clientId)
    ) {
      let parentClientIdValue = null;
      if (type === "tag") {
        parentClientIdValue = tagParentClientRef.current.id;
        setTagParentClientErrorMessage(undefined);
      }
      const params: TagRemoveParentClientParams = {
        clientId: clientId,
        parentClientId: parentClientIdValue,
      };
      tagParentClientApi(params);
    }
  };

  const handleTagButtonClick = () => {
    if (isEmpty(tagParentClientRef.current)) {
      setTagParentClientErrorMessage(
        fetchLabel(
          createClientSiteCoreKeys.searchClientTitle,
          createClientSiteCoreKeys.pleaseSelectAParentClientToProceed,
        ),
      );
      return;
    }
    callTagParentClientApi("tag");
  };

  const handleTagParentClient = () => {
    setShowTagParentModal(true);
  };

  const handleClientSelectedFromSearch = (row: RowProps) => {
    tagParentClientRef.current = row as unknown as ClientResultRowValue;
  };

  const handleRemoveParentConfirmationModal = () => {
    setShowRemoveParentConfirmationModal(true);
    removeParentUseRef.current = true;
  };

  const hideRemoveParentConfirmationModal = () => {
    setShowRemoveParentConfirmationModal(false);
    removeParentUseRef.current = false;
  };

  const handleRemoveParentClient = () => {
    callTagParentClientApi("remove");
    setShowRemoveParentConfirmationModal(false);
  };

  const handleCreateClientProfileChange = (
    _event: React.SyntheticEvent,
    expanded: boolean,
  ) => {
    setProfileAccordionStatus(expanded);
  };

  const handleCreateClientMailingAddressChange = (
    _event: React.SyntheticEvent,
    expanded: boolean,
  ) => {
    setMailingAddressAccordionStatus(expanded);
  };

  const handleExpandCollapseAllChange = () => {
    setProfileAccordionStatus(!allAccordionStatus);
    setAllAccordionStatus(!allAccordionStatus);
    setMailingAddressAccordionStatus(!allAccordionStatus);
    setContactListAccordionStatus(!allAccordionStatus);
    setBillingAndPOAccordionStatus(!allAccordionStatus);
    setDocumentAccordionStatus(!allAccordionStatus);
    setClientSpecificContentAccordionStatus(!allAccordionStatus);
    setClientSpecificActivitiesAccordionStatus(!allAccordionStatus);
  };

  useEffect(() => {
    setAllAccordionStatus(
      profileAccordionStatus ||
        mailingAddressAccordionStatus ||
        contactListAccordionStatus ||
        billingAndPOAccordionStatus ||
        clientSpecificContentAccordionStatus,
    );
  }, [
    profileAccordionStatus,
    mailingAddressAccordionStatus,
    contactListAccordionStatus,
    billingAndPOAccordionStatus,
    clientSpecificContentAccordionStatus,
  ]);

  const renderHeader = (label: string) => {
    return <AccordionHeaderText label={label} />;
  };

  const handleShowContactMinimumModalClose = () => {
    if (showContactMinimumModal) {
      setShowContactMinimumModal(false);
    }
  };

  const handleShowTagParentClientContactRemovalModalClose = () => {
    if (showTagParentClientContactRemovalModal) {
      setShowTagParentClientContactRemovalModal(false);
    }
  };

  const handleShowContactMinimumModalPrimaryButtonClick = () => {
    setShowContactMinimumModal(false);
    clientContactMainRef.current?.handleAddContactClick();
  };

  const handleExitConfirmationModalClose = () => {
    dispatch(
      setNavigationDataClicked({
        ...navigationData,
        onNavigationClicked: false,
      }),
    );
    if (showExitConfirmationModal) {
      setShowExitConfirmationModal(false);
    }
  };

  const handleExitConfirmationModalPrimaryButtonClick = () => {
    dispatch(
      setNavigationDataClicked({
        ...navigationData,
        onNavigationClicked: false,
      }),
    );
    clearFormAndFields();
    dispatch(setIsDataModifiedCurrentPage(false));
    navigate(navigationData.navigationPath);
    setShowExitConfirmationModal(false);
  };

  const handleCancelModalClose = () => {
    if (showCancelModal || showSaveModal) {
      setShowCancelModal(false);
      setShowSaveModal(false);
    }
  };

  const clearFormAndFields = () => {
    reset();
    setIsFormReset(!isFormReset);
    setClientCode("");
    tagParentClientRef.current = undefined;
  };

  const handleCloseModalPrimaryButtonClick = () => {
    setShowCancelModal(false);
    navigate(-1);
    dispatch(setIsDataModifiedCurrentPage(false));
  };
  const handleCancelModalPrimaryButtonClick = () => {
    setShowCancelModal(false);
    clearFormAndFields();
    navigate(-1);
    dispatch(setIsDataModifiedCurrentPage(false));
  };

  const handleSaveModalPrimaryButtonClick = () => {
    setShowSaveModal(false);
    if (!checkTaxVatPostalValidation()) {
      checkErrorsAndExpandSection();
      return;
    }

    handleSubmit(handleSaveConfirmClick)();
  };
  const billingCountryModalClose = () => {
    setShowBillingCountryModal(false);
  };

  const handleBillingCountryModalClick = () => {
    setShowBillingCountryModal(false);
    handleSubmit(handleContinueClick)();
  };

  const onContactApiSuccess = (clientContactsData: ClientContact[]) => {
    const anyClientContact = clientContactsData.find((contact) =>
      isContactBelongCurrentClient(contact),
    );
    setHasSelfContact(isNotEmpty(anyClientContact));
    setIsNewContactAdded(true);
  };

  useEffect(() => {
    if (isClientHierarchySuccess) {
      setShowHierarchyModal(true);
    }
  }, [isClientHierarchySuccess]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isClientHierarchyPending,
      }),
    );
  }, [isClientHierarchyPending]);

  useEffect(() => {
    if (isClientHierarchyError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
    }
  }, [isClientHierarchyError]);

  const handleViewHierarchyClick = () => {
    getClientHierarchyData(clientId);
  };

  const handleUpdateWarningPopup = () => {
    if (showUpdateWarningPopup) {
      setShowUpdateWarningPopup(false);
    }
  };

  const renderFooterButtons = () => {
    return (
      <MuiBox className="container-bottom">
        {isEditClient ? (
          <>
            <MuiButtonSecondary
              id={COMPONENT_IDS.CLOSE_BUTTON}
              className="bottom-button-clear"
              onClick={handleCancelClick}
            >
              {commonLabels.close}
            </MuiButtonSecondary>
            <MuiButtonPrimary
              id={COMPONENT_IDS.SAVE_BUTTON}
              className="bottom-button-continue"
              onClick={handleUpdateClick}
            >
              {commonLabels.update}
            </MuiButtonPrimary>
          </>
        ) : (
          <>
            {clientCode ? (
              <MuiButtonSecondary
                id={COMPONENT_IDS.CLOSE_BUTTON}
                className="bottom-button-clear"
                onClick={handleCloseClick}
              >
                {commonLabels.close}
              </MuiButtonSecondary>
            ) : (
              <MuiButtonSecondary
                id={COMPONENT_IDS.CANCEL_BUTTON}
                className="bottom-button-clear"
                onClick={handleCancelClick}
              >
                {commonLabels.cancel}
              </MuiButtonSecondary>
            )}
            {clientCode ? (
              <MuiButtonPrimary
                id={COMPONENT_IDS.SAVE_BUTTON}
                className="bottom-button-continue"
                onClick={handleSaveClick}
              >
                {commonLabels.save}
              </MuiButtonPrimary>
            ) : (
              <MuiButtonPrimary
                id={COMPONENT_IDS.CONTINUE_BUTTON}
                className="bottom-button-continue"
                onClick={handleBillingModal}
              >
                {commonLabels.continue}
              </MuiButtonPrimary>
            )}
          </>
        )}
      </MuiBox>
    );
  };

  return (
    <PermissionProtectedRoute
      checkCountryLevelAccessOnly //will remove this props once we have page level permission for client module
      hasCountryLevelAccess={isEditClient ? location?.state?.clientId : true}
    >
      <FormProvider {...methods}>
        <CreateClientWrapper>
          <CreateClientHeader onViewHierarchyClick={handleViewHierarchyClick} />
          <CreateClientSemiHeader
            onRemoveParentClientClick={handleRemoveParentConfirmationModal}
            onTagParentClientClick={handleTagParentClient}
            clientCode={clientCode}
            clientName={clientData?.data?.profile?.clientName}
            parentClient={selectedParentClient?.companyName}
            parentClientCode={selectedParentClient?.clientCode}
            isBillingConfigurationSaved={isBillingConfigurationSaved}
            isParentClientRemovalAllowed={isParentClientRemovalAllowed}
          />
          <MuiGrid className="header-collapse">
            <MuiButtonTertiary
              id={COMPONENT_IDS.EXPAND_COLLAPSE_BUTTON}
              onClick={handleExpandCollapseAllChange}
            >
              {allAccordionStatus
                ? commonLabels.collapseAll
                : commonLabels.expandAll}
              {allAccordionStatus ? getRemoveIcon() : getAddIcon()}
            </MuiButtonTertiary>
          </MuiGrid>

          {((isCreateClientError && createClientError) ||
            (isUpdateClientError && updateClientError) ||
            isNotEmpty(duplicateErrorMessage)) && (
            <ErrorWarning
              error={createClientError || updateClientError}
              localErrorMessage={duplicateErrorMessage}
            />
          )}

          <MuiBox className="separator-space" />

          <AccordionHeader
            title={renderHeader(
              fetchComponentTitle(
                createClientSiteCoreKeys.createClientProfileTitle,
              ),
            )}
            onChange={handleCreateClientProfileChange}
            isExpanded={profileAccordionStatus}
            formKey="createClientProfileForm"
          >
            <CreateClientProfileForm
              clientCode={clientCode}
              countryVatPostalData={countryVatPostalData}
              isFormReset={isFormReset}
            />
          </AccordionHeader>

          <MuiBox className="separator-space" />

          <AccordionHeader
            title={renderHeader(
              isRetailClient(clientTypeId)
                ? fetchLabel(
                    createClientSiteCoreKeys.clientMailingAddressTitle,
                    createClientSiteCoreKeys.individualMailingAddressSectionHeading,
                  )
                : fetchComponentTitle(
                    createClientSiteCoreKeys.clientMailingAddressTitle,
                  ),
            )}
            onChange={handleCreateClientMailingAddressChange}
            isExpanded={mailingAddressAccordionStatus}
            formKey="createClientMailingAddressForm"
          >
            <CreateClientMailingAddressForm
              countryVatPostalData={countryVatPostalData}
              isFormReset={isFormReset}
            />
          </AccordionHeader>
          <MuiBox className="separator-space" />

          {clientCode && (
            <>
              <PermissionWrapper
                permission={MODULE_PERMISSIONS.EDIT_CLIENT_CONTACTS}
              >
                <ClientContactMain
                  parentAccordionStatus={contactListAccordionStatus}
                  onAccordionChange={(expanded) => {
                    setContactListAccordionStatus(expanded);
                  }}
                  getClientContactData={(clientContactsData) => {
                    setClientContactsData(clientContactsData);
                    onContactApiSuccess(clientContactsData);
                  }}
                  ref={clientContactMainRef}
                  isAddButtonVisible={true}
                />
              </PermissionWrapper>

              <MuiBox className="separator-space" />

              <PermissionWrapper
                permission={MODULE_PERMISSIONS.EDIT_CLIENT_DOCUMENTS}
              >
                <ClientDocumentMain
                  parentAccordionStatus={documentAccordionStatus}
                  onAccordionChange={(expanded) => {
                    setDocumentAccordionStatus(expanded);
                  }}
                />
              </PermissionWrapper>

              <MuiBox className="separator-space" />

              <PermissionWrapper
                permission={
                  isEditClient
                    ? MODULE_PERMISSIONS.EDIT_CLIENT_BILLING_CONFIG
                    : MODULE_PERMISSIONS.ADD_CLIENT_BILLING_CONFIG
                }
              >
                <ClientBillingPurchaseOrderMain
                  parentAccordionStatus={billingAndPOAccordionStatus}
                  onAccordionChange={(expanded) => {
                    setBillingAndPOAccordionStatus(expanded);
                  }}
                  isNewContactAdded={isNewContactAdded}
                  setIsNewContactAdded={setIsNewContactAdded}
                  triggerAddContactModal={() => {
                    clientContactMainRef.current?.handleAddContactClick();
                  }}
                />
              </PermissionWrapper>

              <MuiBox className="separator-space" />

              <PermissionWrapper
                permission={MODULE_PERMISSIONS.EDIT_CLIENT_SPECIFIC_CONTENT}
              >
                <ClientSpecificContentMain
                  parentAccordionStatus={clientSpecificContentAccordionStatus}
                  onAccordionChange={(expanded) => {
                    setClientSpecificContentAccordionStatus(expanded);
                  }}
                />
              </PermissionWrapper>

              <MuiBox className="separator-space" />

              <PermissionWrapper
                permission={MODULE_PERMISSIONS.EDIT_CLIENT_SPECIFIC_ACTIVITIES}
              >
                <CreateClientActivityAndDeliverableMain
                  parentAccordionStatus={
                    clientSpecificActivitiesAccordionStatus
                  }
                  onAccordionChange={(expanded) => {
                    setClientSpecificActivitiesAccordionStatus(expanded);
                  }}
                />
              </PermissionWrapper>
              <MuiBox className="separator-space" />
            </>
          )}

          {renderFooterButtons()}
          {showCancelModal && (
            <WarningConfirmationModal
              labelContent={commonLabels.discardChangesGivenInfoWillLostText}
              show={showCancelModal}
              modalTitle={commonLabels.confirmation}
              onClose={handleCancelModalClose}
              onPrimaryButtonClick={
                clientCode
                  ? handleCloseModalPrimaryButtonClick
                  : handleCancelModalPrimaryButtonClick
              }
              primaryButtonTitle={commonLabels.yes}
              secondaryButtonTitle={commonLabels.no}
            />
          )}
          {showSaveModal && (
            <WarningConfirmationModal
              labelContent={commonLabels.saveButtonActionText}
              show={showSaveModal}
              modalTitle={commonLabels.confirmation}
              onClose={handleCancelModalClose}
              onPrimaryButtonClick={handleSaveModalPrimaryButtonClick}
              primaryButtonTitle={commonLabels.yes}
              secondaryButtonTitle={commonLabels.no}
            />
          )}

          {showBillingCountryModal && (
            <MuiModal
              id={COMPONENT_IDS.CONFIRMATION_MODAL}
              open={showBillingCountryModal}
              onClose={billingCountryModalClose}
              title={commonLabels.warning}
              primaryButtonTitle={commonLabels.continue}
              secondaryButtonTitle={commonLabels.cancel}
              onPrimaryButtonClick={handleBillingCountryModalClick}
              onSecondaryButtonClick={billingCountryModalClose}
            >
              <MuiBox>
                <MuiTypography
                  id="modal-description"
                  variant="h3"
                  component={"h3"}
                  className="confirm-message"
                >
                  {fetchLabel(
                    createClientSiteCoreKeys.clientMailingAddressTitle,
                    createClientSiteCoreKeys.warningBillingCountry,
                  )}
                </MuiTypography>
              </MuiBox>
            </MuiModal>
          )}

          {showTagParentModal && (
            <MuiModal
              id={COMPONENT_IDS.TAG_PARENT_MODAL}
              hasLargeContent
              open={showTagParentModal}
              onClose={handleTagParentClientModalClose}
              title={fetchLabel(
                createClientSiteCoreKeys.searchClientTitle,
                createClientSiteCoreKeys.searchParentClient,
              )}
              primaryButtonTitle={commonLabels.tag}
              secondaryButtonTitle={commonLabels.cancel}
              onPrimaryButtonClick={handleTagButtonClick}
              onSecondaryButtonClick={handleTagParentClientModalClose}
              footerErrorMessage={tagParentClientErrorMessage}
            >
              <CreateClientWrapper>
                <ClientSearch
                  showAsModal={true}
                  onRowSelected={handleClientSelectedFromSearch}
                />
              </CreateClientWrapper>
            </MuiModal>
          )}
          {showRemoveParentConfirmationModal && (
            <MuiModal
              id={COMPONENT_IDS.REMOVE_PARENT_MODAL}
              open={showRemoveParentConfirmationModal}
              onClose={hideRemoveParentConfirmationModal}
              title={commonLabels.confirmation}
              primaryButtonTitle={commonLabels.yes}
              secondaryButtonTitle={commonLabels.no}
              onPrimaryButtonClick={handleRemoveParentClient}
              onSecondaryButtonClick={hideRemoveParentConfirmationModal}
            >
              <RemoveParentConfirmatoryPopupWrapper>
                <MuiTypography
                  id="remove-parent-modal-description"
                  variant="h3"
                  component={"h3"}
                  className="confirmatory-message"
                >
                  {fetchLabel(
                    keyClientDetailsSiteCoreKeys.keyClientDetailsHeader,
                    keyClientDetailsSiteCoreKeys.removeParentClientConfirmationMessage,
                  )}
                </MuiTypography>
              </RemoveParentConfirmatoryPopupWrapper>
            </MuiModal>
          )}

          {showContactMinimumModal && (
            <WarningConfirmationModal
              labelContent={getContactsRichLabel(
                createClientSiteCoreKeys.createContactToProceed,
              )}
              isLabelHTMLContent={true}
              show={showContactMinimumModal}
              onClose={handleShowContactMinimumModalClose}
              onPrimaryButtonClick={
                handleShowContactMinimumModalPrimaryButtonClick
              }
              primaryButtonTitle={fetchLabel(
                createClientSiteCoreKeys.contactsTitle,
                createClientSiteCoreKeys.addContact,
              )}
              secondaryButtonTitle={commonLabels.cancel}
            />
          )}

          {showTagParentClientContactRemovalModal && (
            <WarningConfirmationModal
              labelContent={fetchLabel(
                keyClientDetailsHeader,
                removeTheParentClientAsAtleastOneContactIsRequired,
              )}
              show={showTagParentClientContactRemovalModal}
              modalTitle={commonLabels.confirmation}
              onClose={handleShowTagParentClientContactRemovalModalClose}
              onPrimaryButtonClick={
                handleShowTagParentClientContactRemovalModalClose
              }
              primaryButtonTitle={commonLabels.ok}
              showSecondaryButton={false}
            />
          )}

          {showExitConfirmationModal && (
            <WarningConfirmationModal
              labelContent={commonLabels.leavingPageActionText}
              show={showExitConfirmationModal}
              onClose={handleExitConfirmationModalClose}
              onPrimaryButtonClick={
                handleExitConfirmationModalPrimaryButtonClick
              }
              primaryButtonTitle={commonLabels.yes}
              secondaryButtonTitle={commonLabels.no}
            />
          )}
          {showScheduleTotalAmountWarning.isOpen && (
            <WarningConfirmationModal
              show={showScheduleTotalAmountWarning.isOpen}
              modalTitle={commonLabels.warning}
              onClose={handleScheduleAmountWarningModalClose}
              onPrimaryButtonClick={handleScheduleAmountWarningModalClose}
              showSecondaryButton={false}
              primaryButtonTitle={commonLabels.ok}
              labelContent={
                showScheduleTotalAmountWarning.isPercentage
                  ? commonValidationMessages.totalPercentageMustEqual100
                  : commonBillingConfigurationDetails.totalAmountMustEqualProgramFeeErrorMessage
              }
            />
          )}

          {showHierarchyModal && (
            <ClientSearchResultHierarchy
              open={showHierarchyModal}
              onClose={() => setShowHierarchyModal(false)}
              hierarchyData={clientHierarchyResult?.data}
              clientName={clientData?.data?.profile.clientName!}
            />
          )}

          {showUpdateWarningPopup && (
            <WarningConfirmationModal
              show={showUpdateWarningPopup}
              onClose={() => {
                setShowUpdateWarningPopup(false);
              }}
              onPrimaryButtonClick={handleUpdateWarningPopup}
              showSecondaryButton={false}
              primaryButtonTitle={commonLabels.ok}
              labelContent={warningErrorMessage}
            />
          )}
        </CreateClientWrapper>
      </FormProvider>
    </PermissionProtectedRoute>
  );
};

export default CreateClient;
